import { motion } from 'motion/react';
import { Lock, Plus, Key, ShieldCheck, RotateCw } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { KpiCard } from '@/components/viz/KpiCard';
import { useEffect, useState } from 'react';
import { Signature, ShieldCheck as VerifyIcon } from 'lucide-react';
import {
  generatePqKeyPair,
  listPqKeyPairs,
  pqSign as pqSignApi,
  pqVerify as pqVerifyApi,
  type PqKeyPair,
  type PqKeyPairRow,
  type PqSignResult,
} from '@/lib/api';

interface PqKey {
  id: string;
  algo: 'crystals_kyber_768' | 'crystals_kyber_1024' | 'crystals_dilithium_2' | 'crystals_dilithium_3' | 'crystals_dilithium_5';
  purpose: 'audit_chain' | 'breach_notification' | 'tokenisation_kem' | 'workload_identity';
  provider: 'liboqs' | 'cloudflare_circl' | 'aws_kms_pq_pre_release';
  activeFrom: string;
  rotated?: string;
  status: 'active' | 'rotated' | 'revoked';
}

const KEYS: PqKey[] = [
  { id: 'pq-001', algo: 'crystals_dilithium_3', purpose: 'audit_chain', provider: 'liboqs', activeFrom: '2026-04-01', status: 'active' },
  { id: 'pq-002', algo: 'crystals_dilithium_5', purpose: 'breach_notification', provider: 'liboqs', activeFrom: '2026-04-01', status: 'active' },
  { id: 'pq-003', algo: 'crystals_kyber_768', purpose: 'tokenisation_kem', provider: 'cloudflare_circl', activeFrom: '2026-04-15', status: 'active' },
  { id: 'pq-004', algo: 'crystals_kyber_1024', purpose: 'tokenisation_kem', provider: 'liboqs', activeFrom: '2026-05-01', status: 'active' },
  { id: 'pq-005', algo: 'crystals_dilithium_2', purpose: 'workload_identity', provider: 'liboqs', activeFrom: '2026-03-01', rotated: '2026-04-15', status: 'rotated' },
];

function rowToCard(r: PqKeyPairRow): PqKey {
  const algo = r.algorithm as PqKey['algo'];
  const purpose = r.purpose as PqKey['purpose'];
  const provider = (r.libraryProvider as PqKey['provider']) || 'liboqs';
  const status: PqKey['status'] = r.revokedAt ? 'revoked' : r.rotatedAt ? 'rotated' : 'active';
  return {
    id: r.id,
    algo,
    purpose,
    provider,
    activeFrom: r.activeFrom.slice(0, 10),
    rotated: r.rotatedAt ? r.rotatedAt.slice(0, 10) : undefined,
    status,
  };
}

export function FrontierPq() {
  const [flipped, setFlipped] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState<PqKeyPair | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [liveKeys, setLiveKeys] = useState<PqKey[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    listPqKeyPairs(20)
      .then((res) => {
        if (cancelled || res.keyPairs.length === 0) return;
        setLiveKeys(res.keyPairs.map(rowToCard));
        setLiveRows(res.keyPairs);
      })
      .catch(() => {
        /* keep mocks */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const keys = liveKeys ?? KEYS;

  const [signing, setSigning] = useState(false);
  const [lastSignature, setLastSignature] = useState<PqSignResult | null>(null);
  const [signError, setSignError] = useState<string | null>(null);
  // Wave 5 (2026-05-27) - re-verify any signature panel
  const [verifyPayload, setVerifyPayload] = useState('');
  const [verifySignature, setVerifySignature] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<{ valid: boolean; hash: string } | null>(null);
  // Stash the raw live rows separately - PqKeyCard uses the adapted
  // PqKey shape but verify needs the publicKeyB64 + KMS ref from
  // the live registry row.
  const [liveRows, setLiveRows] = useState<PqKeyPairRow[]>([]);

  async function handleVerify(): Promise<void> {
    const row =
      liveRows.find((r) => r.revokedAt == null && r.rotatedAt == null && /dilithium/i.test(r.algorithm)) ??
      liveRows[0];
    if (!row) {
      setSignError('No active Dilithium key available to verify against.');
      return;
    }
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await pqVerifyApi({
        algorithm: row.algorithm.toLowerCase().startsWith('crystals_')
          ? row.algorithm.toLowerCase()
          : `crystals_${row.algorithm.toLowerCase()}`,
        publicKeyB64: row.publicKeyB64,
        privateKeyKmsRef: row.privateKeyKmsRef,
        payload: verifyPayload,
        signatureB64: verifySignature,
      });
      setVerifyResult({ valid: res.valid, hash: res.payloadHash });
    } catch (e) {
      setSignError(e instanceof Error ? e.message : 'verify failed');
    } finally {
      setVerifying(false);
    }
  }
  async function handleSignDemo(keyId: string): Promise<void> {
    setSigning(true);
    setSignError(null);
    try {
      const payload = `demo-audit-block-${Date.now()}`;
      const result = await pqSignApi({
        keyId,
        targetType: 'audit_chain_block',
        targetId: payload,
        payload,
      });
      setLastSignature(result);
    } catch (e) {
      setSignError(e instanceof Error ? e.message : 'sign failed');
    } finally {
      setSigning(false);
    }
  }

  async function handleGenerate(): Promise<void> {
    setError(null);
    setGenerating(true);
    try {
      const result = await generatePqKeyPair({
        algorithm: 'crystals_dilithium_3',
        purpose: 'audit_chain',
        libraryProvider: 'surrogate_test_only',
      });
      setGenerated(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'PQ key generation failed');
    } finally {
      setGenerating(false);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Post-Quantum Key Vault"
        subtitle="NIST PQC Round-3 finalists. CRYSTALS-Kyber for KEM, CRYSTALS-Dilithium for signatures. Private keys live in customer-VPC KMS - controller stores only kms:// references."
        badges={[{ label: 'CRYSTALS-Kyber', variant: 'glass' }, { label: 'CRYSTALS-Dilithium', variant: 'glass' }, { label: 'libOQS · CIRCL', variant: 'info' }]}
        actions={
          <Button data-action="disc.frontier_pq.a03" size="sm" onClick={handleGenerate} disabled={generating}>
            <Plus className="h-4 w-4" /> {generating ? 'Generating…' : 'Generate key pair'}
          </Button>
        }
      />

      {generated && (
        <GlassCard className="p-5 mb-4 border-primary glow-low">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2"><Key className="h-4 w-4 text-primary" /> New PQ key pair issued</CardTitle>
            <CardDescription clamp>Private key reference held in KMS; only the public material is plaintext.</CardDescription>
          </CardHeader>
          <div className="grid grid-cols-12 gap-3 items-center glass-lite p-3 rounded-md text-xs">
            <div className="col-span-3"><div className="text-[9px] uppercase text-muted-foreground">keyId</div><code className="font-mono">{generated.keyId.slice(0, 18)}…</code></div>
            <div className="col-span-3"><div className="text-[9px] uppercase text-muted-foreground">algorithm</div><code className="font-mono">{generated.algorithm}</code></div>
            <div className="col-span-3"><div className="text-[9px] uppercase text-muted-foreground">purpose</div><code className="font-mono">{generated.purpose}</code></div>
            <div className="col-span-3"><div className="text-[9px] uppercase text-muted-foreground">provider</div><code className="font-mono">{generated.libraryProvider}</code></div>
            <div className="col-span-12 mt-2"><div className="text-[9px] uppercase text-muted-foreground">KMS reference (private key never leaves customer VPC)</div><code className="font-mono opacity-80">{generated.privateKeyKmsRef}</code></div>
          </div>
        </GlassCard>
      )}

      {error && (
        <GlassCard className="p-4 mb-4 border-destructive">
          <p className="text-xs text-destructive">PQ error: {error}</p>
        </GlassCard>
      )}

      {lastSignature && (
        <GlassCard className="p-5 mb-4 border-success glow-low">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2">
              <Signature className="h-4 w-4 text-success" /> Demo payload signed
            </CardTitle>
            <CardDescription clamp>
              Dilithium signature attached to a synthetic audit-chain block - verifiable via the
              public key. Real audit blocks sign automatically on commit.
            </CardDescription>
          </CardHeader>
          <div className="grid grid-cols-12 gap-3 items-center glass-lite p-3 rounded-md text-xs">
            <div className="col-span-4">
              <div className="text-[9px] uppercase text-muted-foreground">signatureId</div>
              <code className="font-mono">{lastSignature.signatureId.slice(0, 22)}…</code>
            </div>
            <div className="col-span-4">
              <div className="text-[9px] uppercase text-muted-foreground">algorithm</div>
              <code className="font-mono">{lastSignature.algorithm}</code>
            </div>
            <div className="col-span-4">
              <div className="text-[9px] uppercase text-muted-foreground">targetType</div>
              <code className="font-mono">{lastSignature.targetType}</code>
            </div>
            <div className="col-span-12 mt-2">
              <div className="text-[9px] uppercase text-muted-foreground">payloadHash (SHA-256)</div>
              <code className="font-mono opacity-80 break-all">{lastSignature.payloadHash}</code>
            </div>
            <div className="col-span-12">
              <div className="text-[9px] uppercase text-muted-foreground">signatureB64 (truncated)</div>
              <code className="font-mono opacity-80 break-all text-[10px]">
                {lastSignature.signatureB64.slice(0, 96)}…
              </code>
            </div>
          </div>
        </GlassCard>
      )}

      {signError && (
        <GlassCard className="p-4 mb-4 border-warning">
          <p className="text-xs text-warning">Sign failed: {signError}</p>
        </GlassCard>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard
          label="Active key pairs"
          value={keys.filter((k) => k.status === 'active').length}
          icon={Key}
          hint={`across ${new Set(keys.map((k) => k.purpose)).size} purposes`}
        />
        <KpiCard label="Audit blocks signed" value={184_502} icon={ShieldCheck} hint="Dilithium-3" />
        <KpiCard label="KEM operations (24h)" value={1_247} icon={Lock} hint="Kyber-768/1024" />
        <KpiCard label="Avg rotation cadence" value={45} format="duration" icon={RotateCw} hint="days · per purpose" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {keys.map((k, i) => (
          <PqKeyCard
            key={k.id}
            pqKey={k}
            flipped={flipped === k.id}
            onFlip={() => setFlipped(flipped === k.id ? null : k.id)}
            delay={i * 0.05}
            onSign={k.status === 'active' && k.algo.startsWith('crystals_dilithium') ? () => void handleSignDemo(k.id) : undefined}
            signing={signing}
          />
        ))}
      </div>

      {/* Wave 5 (2026-05-27) - verify a signature against the registered key. */}
      <GlassCard className="p-5 mt-4">
        <CardHeader className="p-0 mb-3">
          <CardTitle className="flex items-center gap-2">
            <VerifyIcon className="h-4 w-4 text-primary" /> Verify a signature (advanced)
          </CardTitle>
          <CardDescription>
            Check whether a Dilithium signature is valid for a payload, using the active key in
            the registry. Wraps <code className="font-mono text-[10px]">POST /v1/frontier/pq/verify</code>.
          </CardDescription>
        </CardHeader>
        <div className="grid grid-cols-12 gap-3 text-xs items-end">
          <div className="col-span-6">
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Payload (plaintext)</div>
            <input
              type="text"
              value={verifyPayload}
              onChange={(e) => setVerifyPayload(e.target.value)}
              placeholder="e.g. demo-audit-block-1779861234567"
              className="glass-lite w-full px-3 py-1.5 rounded-md text-xs border border-border/40 focus:outline-none focus:border-primary"
            />
          </div>
          <div className="col-span-4">
            <Button
              data-action="disc.frontier_pq.a01"
              className="w-full"
              size="sm"
              onClick={() => void handleVerify()}
              disabled={verifying || !verifyPayload || !verifySignature}
            >
              <VerifyIcon className="h-3.5 w-3.5 mr-1.5" />
              {verifying ? 'Verifying…' : 'Verify signature'}
            </Button>
          </div>
          <div className="col-span-2">
            {verifyResult && (
              <Badge variant={verifyResult.valid ? 'success' : 'critical'} className="text-[9px]">
                {verifyResult.valid ? 'VALID' : 'INVALID'}
              </Badge>
            )}
          </div>
          <div className="col-span-12">
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Signature (base64)</div>
            <input
              type="text"
              value={verifySignature}
              onChange={(e) => setVerifySignature(e.target.value)}
              placeholder="Paste signatureB64 from a prior Sign demo result"
              className="glass-lite w-full px-3 py-1.5 rounded-md text-xs font-mono border border-border/40 focus:outline-none focus:border-primary"
            />
          </div>
          {verifyResult && (
            <div className="col-span-12 glass-lite rounded-md p-2 text-[11px] text-muted-foreground mt-1">
              payloadHash: <code className="font-mono">{verifyResult.hash}</code>
            </div>
          )}
        </div>
      </GlassCard>
    </PageContainer>
  );
}

function PqKeyCard({
  pqKey: k,
  flipped,
  onFlip,
  delay,
  onSign,
  signing,
}: {
  pqKey: PqKey;
  flipped: boolean;
  onFlip: () => void;
  delay: number;
  onSign?: () => void;
  signing?: boolean;
}) {
  const isKem = k.algo.startsWith('crystals_kyber');
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="perspective-1000"
      style={{ perspective: '1000px' }}
    >
      <motion.div
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.7, ease: [0.32, 0.72, 0, 1] }}
        style={{ transformStyle: 'preserve-3d', position: 'relative' }}
        className="cursor-pointer"
        onClick={onFlip}
      >
        {/* Front face */}
        <div style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}>
          <GlassCard shine className="p-5 h-full min-h-[200px] flex flex-col">
            <div className="flex items-start justify-between mb-3">
              <div className={`h-10 w-10 rounded-md flex items-center justify-center ${isKem ? 'bg-info/20 text-info' : 'bg-primary/20 text-primary'}`}>
                {isKem ? <Key className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
              </div>
              <Badge variant={k.status === 'active' ? 'success' : k.status === 'rotated' ? 'glass' : 'destructive'} className="text-[9px]">{k.status}</Badge>
            </div>
            <CardTitle className="text-sm flex items-baseline gap-2">
              {k.algo.replace('crystals_', 'CRYSTALS-').replace(/_/g, '-').replace('kyber', 'Kyber').replace('dilithium', 'Dilithium')}
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              Purpose · <code>{k.purpose}</code>
            </CardDescription>
            <div className="mt-auto pt-3 border-t border-border/30 text-[10px] text-muted-foreground">
              <div>Active from <span className="tabular text-foreground/80">{k.activeFrom}</span></div>
              <div>Provider · <code className="text-foreground/80">{k.provider}</code></div>
              <div className="mt-1 italic">Click to flip</div>
              {onSign && (
                <button
                  data-action="disc.frontier_pq.a02"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSign();
                  }}
                  disabled={signing}
                  className="mt-2 w-full glass-lite px-2 py-1.5 rounded-md hover:bg-primary/10 transition-colors text-[11px] text-primary font-medium inline-flex items-center justify-center gap-1.5"
                  title="Produce a Dilithium signature over a synthetic audit-chain block"
                >
                  <Signature className="h-3 w-3" />
                  {signing ? 'Signing…' : 'Sign demo payload'}
                </button>
              )}
            </div>
          </GlassCard>
        </div>

        {/* Back face */}
        <div
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0,
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          <GlassCard className="p-5 h-full min-h-[200px] aurora">
            <CardTitle className="text-sm mb-3">Public key fingerprint</CardTitle>
            {/*
              Phase 0 item 6 (2026-05-26) - removed the Math.random() fake-
              SHA-256 fingerprint that re-rolled on every render. Replaced
              with a deterministic placeholder derived from the key id
              (stable across renders, obvious it's not a real signature).
              When the Frontier PQ surface is wired to a real KMS endpoint
              (Phase 4), this becomes a deterministic hash of the actual
              public-key bytes via SubtleCrypto.
            */}
            <code className="block text-[10px] font-mono bg-card/40 p-2 rounded break-all leading-relaxed text-muted-foreground">
              SHA-256: not configured - Frontier KMS pairing required
            </code>
            <div className="mt-3 space-y-1.5 text-[11px]">
              <div className="flex justify-between"><span className="text-muted-foreground">Key length</span><span className="tabular">{isKem ? '1184' : '2420'} bytes</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Signature length</span><span className="tabular">{isKem ? 'N/A (KEM)' : '3293 bytes'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">NIST level</span><span className="tabular">{k.algo.includes('5') ? '5' : k.algo.includes('3') ? '3' : k.algo.includes('1024') ? '5' : k.algo.includes('768') ? '3' : '2'}</span></div>
            </div>
            <Button variant="glass" size="sm" className="w-full mt-3"><RotateCw className="h-3 w-3" /> Rotate</Button>
          </GlassCard>
        </div>
      </motion.div>
    </motion.div>
  );
}
